/**
 * The photo evidence for one breaker, as a grid of named slots.
 *
 * Every slot is a specific shot — `CB-ON-BRK`, `MV-Current-PQA` — and tapping it
 * captures against *that* shot code. This is the whole design: a photo cannot
 * exist without knowing which breaker and which shot it belongs to, because the
 * sorting problem is created at capture. A camera roll of 8,000 untagged photos
 * cannot be re-sorted afterwards by anyone.
 *
 * Slots come from the shot profiles, so an ACB shows its six states and an MCCB
 * its three without this component knowing either number.
 */

import { memo } from 'react';
import type { CapturedShot, ShotProfile } from '../domain/types.js';
import type { ShotProgress } from '../domain/progress.js';

export interface PhotoGridProps {
  profiles: readonly ShotProfile[];
  captured: Readonly<Record<string, CapturedShot>>;
  progress: ShotProgress;
  onCapture: (profile: ShotProfile) => void;
  disabled?: boolean;
}

/**
 * What a tile says.
 *
 * `viewpoint` is "N/A" on profiles that have no viewpoint — four phase-rotation
 * and earth-loop slots all reading "N/A" tell an engineer nothing about which
 * one is which, and the shot code does.
 */
export function tileLabel(profile: ShotProfile): string {
  const view = (profile.viewpoint ?? '').trim();
  return !view || view.toUpperCase() === 'N/A' ? profile.shotCode : view;
}

/**
 * Splits a shot group by measurement group, when it has more than one.
 *
 * Returns a single unnamed bucket otherwise, so a group like CB Status is not
 * given a heading it does not need.
 */
export function subGroupsOf(
  profiles: readonly ShotProfile[],
): { name: string; profiles: ShotProfile[] }[] {
  const names = [...new Set(profiles.map((p) => (p.measurementGroup ?? '').trim()).filter(Boolean))];
  if (names.length <= 1) return [{ name: '', profiles: [...profiles] }];
  const split = names.map((name) => ({
    name,
    profiles: profiles.filter((p) => (p.measurementGroup ?? '').trim() === name),
  }));
  // A heading above a single tile is noise, and it doubles the height of a
  // grid an engineer scrolls with one thumb. Split only when the headings are
  // carrying more than one shot each — the tile's own label already says which
  // shot it is.
  return split.some((g) => g.profiles.length > 1) ? split : [{ name: '', profiles: [...profiles] }];
}

export const PhotoGrid = memo(function PhotoGrid({
  profiles, captured, progress, onCapture, disabled,
}: PhotoGridProps) {
  const groups = progress.byGroup;

  return (
    <section className="photos" aria-label="Photo evidence">
      <header className="photos-head">
        <h3>Photos</h3>
        <span data-testid="shot-total">
          {progress.captured} of {progress.required}
        </span>
      </header>

      {progress.unexpected.length > 0 && (
        // A photo matching no profile is a mis-tag or a retake against the wrong
        // breaker type. Surfacing it is the only way it ever gets fixed.
        <p className="warn" role="status" data-testid="unexpected">
          {progress.unexpected.length} photo{progress.unexpected.length === 1 ? '' : 's'} match no required shot:{' '}
          {progress.unexpected.join(', ')}
        </p>
      )}

      {groups.map((group) => {
        const inGroup = profiles.filter((p) => p.shotGroup === group.group && p.required);
        return (
          <div className="shot-group" key={group.group}>
            <h4>
              {group.group}
              <span className="count" data-testid={`group-${group.group}`}>
                {group.captured}/{group.required}
              </span>
            </h4>
            {subGroupsOf(inGroup).map(({ name, profiles: tiles }) => (
            <div className="sub-group" key={name || group.group}>
            {/* Meter verification is five reading groups x three sources. Thirty
                tiles labelled only DPM/PQA/BMS are indistinguishable; the
                reading group has to be on screen or the engineer is counting. */}
            {name ? <h5>{name}</h5> : null}
            <div className="slots">
              {tiles.map((profile) => {
                const shot = captured[profile.shotCode];
                const done = Boolean(shot?.photoUrl);
                return (
                  <button
                    key={profile.shotCode}
                    type="button"
                    className="slot"
                    data-done={done}
                    data-shot={profile.shotCode}
                    disabled={disabled}
                    aria-label={
                      done
                        ? `Retake ${profile.measurementGroup} ${profile.viewpoint}`
                        : `Capture ${profile.measurementGroup} ${profile.viewpoint}`
                    }
                    onClick={() => onCapture(profile)}
                  >
                    {done && shot?.photoUrl ? (
                      <img src={shot.photoUrl} alt="" />
                    ) : (
                      <span className="plus" aria-hidden="true">
                        ＋
                      </span>
                    )}
                    <span className="code">{tileLabel(profile)}</span>
                    {done ? (
                      <span className="tick" aria-hidden="true">
                        ✓
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
            </div>
            ))}
          </div>
        );
      })}
    </section>
  );
});
